import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Calendar,
  Clock,
  Users,
  LogOut,
  Menu,
  Bell,
  Settings,
  Building2,
} from 'lucide-react';
import { useAuthStore } from '../store';
import { Avatar } from './ui';
import { ClockButton } from './ClockButton';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { user, tenant, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Schedule', icon: Calendar },
    { path: '/my-shifts', label: 'My Shifts', icon: Clock },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  const adminNavItems = [
    { path: '/admin/requests', label: 'Shift Requests', icon: Bell },
    { path: '/admin/shift-templates', label: 'Permanent Shifts', icon: Clock },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/clock-records', label: 'Clock Records', icon: Clock },
    { path: '/admin/settings', label: 'Admin Settings', icon: Settings },
  ];

  const superAdminNavItems = [
    { path: '/super-admin/tenants', label: 'Tenant Management', icon: Building2 },
  ];

  const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';
  const isSuperAdmin = user?.role === 'SuperAdmin';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-600 flex items-center justify-center text-white">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-900">Shift Scheduler</h1>
              <p className="text-xs text-slate-500">Manage your schedule</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            <p className="px-3 mb-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
              Main
            </p>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                  location.pathname === item.path
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                )}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}

            {isSuperAdmin && (
              <>
                <p className="px-3 mt-6 mb-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Super Admin
                </p>
                {superAdminNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={clsx(
                      'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                      location.pathname === item.path
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                ))}
              </>
            )}
            {isAdmin && (
              <>
                <p className="px-3 mt-6 mb-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Admin
                </p>
                {adminNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={clsx(
                      'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                      location.pathname === item.path
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                ))}
              </>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <Avatar name={user?.display_name || user?.username || 'User'} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {user?.display_name || user?.username}
                </p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200">
          <div className="flex items-center justify-between px-4 py-3 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 rounded-xl text-slate-600 hover:bg-slate-100 lg:hidden"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-3">
              {tenant && (
                <span className="text-sm text-slate-600 font-medium truncate max-w-[180px]" title={tenant.name}>
                  {tenant.name}
                </span>
              )}
              <ClockButton />
              <span className="text-sm text-slate-500">
                {user?.role === 'SuperAdmin' && (
                  <span className="badge badge-purple">Super Admin</span>
                )}
                {user?.role === 'Admin' && (
                  <span className="badge badge-purple">Admin</span>
                )}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
